/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from "react-i18next";
import { Tab } from '@headlessui/react';

import NoOddsLabels       from 'features/markets/components/NoOddsLabels';
import SingleMarkets      from 'features/markets/components/SingleMarkets';
import ReverseForecast    from 'features/markets/components/multiples/ReverseForecast';
import StraightForecast   from 'features/markets/components/multiples/StraightForecast';
import TricastMarkets     from 'features/markets/components/TricastMarkets';

import ForecastTooltip from 'features/markets/components/tooltips/ForecastTooltip';
import TricastTooltip from 'features/markets/components/tooltips/TricastTooltip';
import { cn } from 'lib/utils';
import { getSportColor, getSportLightColor } from 'shared/utils/sport-colors';
import { getMarketTypeByGroupId } from 'shared/utils/markets';
import { vsMarketTypeKey } from 'lib/virtualSportsI18n';

/**
 * Renders the bet area for racing
 *
 * @class      RacingArea (name)
 * @param      {<type>}   props   The properties
 * @return     {boolean}  { description_of_the_return_value }
 */
function RacingArea(props) {
  const { t } = useTranslation();
  const {
    available_market_groups,
    event,
  } = props;

  const sport_id = event?.sport_id ?? event?.sport ?? null;
  if (sport_id == null) return null;
  const sportColor = getSportColor(sport_id);
  const sportLightColor = getSportLightColor(sport_id);

  const racingTabClass = (selected) => cn(
    "py-2 md:py-3 text-center cursor-pointer transition-colors relative text-xs md:text-sm rounded-md border-[3px]",
    selected
      ? "border-[var(--sport-color)] bg-card text-foreground"
      : "border-border bg-card text-foreground hover:border-[var(--sport-color)]"
  );
  const racingSubTabClass = (selected) => cn(
    "py-2 text-center cursor-pointer transition-colors text-xs rounded-md border-[3px]",
    selected
      ? "border-[var(--sport-color)] bg-card text-foreground"
      : "border-border bg-card text-foreground hover:border-[var(--sport-color)]"
  );

  // Check if Show market (503) is supported
  const sMarket = getMarketTypeByGroupId(503, { ...event, available_market_groups });
  const wMarket = getMarketTypeByGroupId(501, { ...event, available_market_groups });
  const pMarket = getMarketTypeByGroupId(502, { ...event, available_market_groups });
  const s_mk_id = sMarket?.market_type_id;
  const w_mk_id = wMarket?.market_type_id;
  const p_mk_id = pMarket?.market_type_id;
  const outcomesList = Array.isArray(event?.outcomes)
    ? event.outcomes
    : event?.outcomes && typeof event.outcomes === 'object'
      ? Object.values(event.outcomes)
      : [];
  const showMarketSupported = !!(
    s_mk_id &&
    outcomesList.some((o) => String(o?.market_type_id) === String(s_mk_id) && o?.odd != null)
  );

  return (
    <div className="racing-area w-full" data-sport-id={sport_id} style={{ '--sport-color': sportColor, '--sport-light-color': sportLightColor }}>
      <Tab.Group>
        <Tab.List className="grid grid-cols-3 gap-1 p-2 border-b border-border">
          <Tab className={({ selected }) => racingTabClass(selected)}>
            <span className="truncate block px-1">{t(`RACING_MARKET_LABEL_SINGLE`)}</span>
          </Tab>
          <Tab className={({ selected }) => racingTabClass(selected)}>
            <span className="truncate block px-1">{t(`RACING_MARKET_LABEL_PAIR`)}</span>
            <ForecastTooltip/>
          </Tab>
          <Tab className={({ selected }) => racingTabClass(selected)}>
            <span className="truncate block px-1">{t(`RACING_MARKET_LABEL_TRIO`)}</span>
            <TricastTooltip/>
          </Tab>
        </Tab.List>
        
        <Tab.Panels>
          <Tab.Panel>
            <div
              className="winners-place-show flex flex-row items-center w-full text-xs md:text-sm border-b border-border bg-muted/60"
            >
              <div className="grid-item w-1/12">
                <div className="head-competitor-code"><span></span></div>
              </div>
              <div className="grid-item w-4/12">
                <div className="head-competitor-name"><span></span></div>
              </div>
              <div className="flex items-center justify-center grid-item w-2/12 py-2">
                <span className="truncate font-semibold text-foreground">
                  {w_mk_id ? t(vsMarketTypeKey(w_mk_id, false), { defaultValue: wMarket?.name || '' }) : ''}
                </span>
              </div>
              <div className="flex items-center justify-center grid-item w-2/12 py-2">
                <span className="truncate font-semibold text-foreground">
                  {p_mk_id ? t(vsMarketTypeKey(p_mk_id, false), { defaultValue: pMarket?.name || '' }) : ''}
                </span>
              </div>
              {showMarketSupported && (
                <div className="flex items-center justify-center grid-item w-2/12 py-2">
                  <span className="truncate font-semibold text-foreground">
                    {s_mk_id ? t(vsMarketTypeKey(s_mk_id, false), { defaultValue: sMarket?.name || '' }) : ''}
                  </span>
                </div>
              )}
            </div>
            <SingleMarkets {...{...event, available_market_groups: available_market_groups}}/>
          </Tab.Panel>
          
          <Tab.Panel>
            <Tab.Group>
              <Tab.List className="grid grid-cols-2 gap-1 p-2 mb-2 border-b border-border">
                <Tab className={({ selected }) => racingSubTabClass(selected)}>
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-arrow-right text-sm"></i>
                    <span className="truncate">{t(`IN_ORDER`)}</span>
                  </div>
                </Tab>
                <Tab className={({ selected }) => racingSubTabClass(selected)}>
                  <div className="flex items-center justify-center gap-1">
                    <i className="bi bi-arrow-left-right text-sm"></i>
                    <span className="truncate">{t(`NOT_IN_ORDER`)}</span>
                  </div>
                </Tab>
              </Tab.List>
              
              <Tab.Panels>
                <Tab.Panel>
                  {available_market_groups.includes(504)? <StraightForecast {...event}/>: <NoOddsLabels />}
                </Tab.Panel>
                <Tab.Panel>
                  {available_market_groups.includes(505)? <ReverseForecast {...event}/>: <NoOddsLabels />}
                </Tab.Panel>
              </Tab.Panels>
            </Tab.Group>
          </Tab.Panel>
          
          <Tab.Panel>
            <TricastMarkets key={`tricast-${sport_id}-${event?.event_id || 'default'}`} {...{...event, sport_id, available_market_groups: available_market_groups}}/>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}

export default RacingArea;

